import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Calendar, Check, X, Mail, Phone, ShieldCheck, RefreshCw } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { AdminArticles } from '../components/AdminArticles';
import { AdminGiftCards } from '../components/AdminGiftCards';
import { AdminSearchSetup } from '../components/AdminSearchSetup';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 20000; // auto-refresh every 20 seconds

const statusStyle = {
  pending: 'text-[#8B7FB8] bg-[#E5DDF0]',
  confirmed: 'text-[#2F7A4E] bg-[#DDEEE1]',
  declined: 'text-[#8A4A4A] bg-[#EED8D8]',
};

const Admin = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [checked, setChecked] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const prevIdsRef = useRef(new Set());

  const loadBookings = useCallback(async (opts = {}) => {
    const { silent = false } = opts;
    if (!silent) setRefreshing(true);
    try {
      const r = await axios.get(`${API}/bookings/admin`, { withCredentials: true });
      const data = r.data || [];
      // Notify on newly arrived bookings after the first successful load
      if (prevIdsRef.current.size > 0) {
        const newOnes = data.filter((b) => !prevIdsRef.current.has(b.booking_id));
        if (newOnes.length > 0) {
          toast({
            title: `${newOnes.length} new request${newOnes.length > 1 ? 's' : ''}`,
            description: 'Someone just booked a session.',
          });
        }
      }
      prevIdsRef.current = new Set(data.map((b) => b.booking_id));
      setBookings(data);
      setLastUpdated(new Date());
    } catch (e) {
      if (!silent) toast({ title: 'Failed to refresh', variant: 'destructive' });
    } finally {
      setRefreshing(false);
    }
  }, [toast]);

  // Initial admin gate + first load
  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/'); return; }
    (async () => {
      try {
        const a = await axios.get(`${API}/bookings/admin/is-admin`, { withCredentials: true });
        if (!a.data?.admin) { navigate('/'); return; }
        setChecked(true);
        await loadBookings({ silent: true });
      } catch (e) {
        navigate('/');
      } finally { setLoading(false); }
    })();
  }, [authLoading, user, navigate, loadBookings]);

  // Auto-poll for new bookings
  useEffect(() => {
    if (!checked) return;
    const id = setInterval(() => { loadBookings({ silent: true }); }, POLL_MS);
    // Refresh instantly when window becomes visible again
    const onVis = () => { if (document.visibilityState === 'visible') loadBookings({ silent: true }); };
    document.addEventListener('visibilitychange', onVis);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVis); };
  }, [checked, loadBookings]);

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/bookings/admin/${id}`, { status }, { withCredentials: true });
      setBookings((prev) => prev.map((b) => b.booking_id === id ? { ...b, status } : b));
      toast({ title: `Marked ${status}`, description: 'The client has been notified (mocked email).' });
    } catch {
      toast({ title: 'Update failed', variant: 'destructive' });
    }
  };

  const filtered = filter === 'all' ? bookings : bookings.filter((b) => b.status === filter);

  const formatUpdated = (d) => {
    if (!d) return '';
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (!checked && loading) {
    return (
      <div className="bg-[#F4EFE7] min-h-screen flex items-center justify-center">
        <div className="text-[#1E1B18]/60 text-sm tracking-[0.2em]">CHECKING ACCESS…</div>
      </div>
    );
  }

  return (
    <div className="bg-[#F4EFE7] min-h-screen">
      <Navbar />

      <section className="pt-32 pb-20 max-w-[1200px] mx-auto px-8">
        <p className="text-[11px] tracking-[0.3em] text-[#8B7FB8] font-medium mb-6 flex items-center gap-2">
          <ShieldCheck size={13} /> ADMIN DASHBOARD
        </p>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <h1 className="font-serif text-5xl lg:text-6xl text-[#1E1B18] leading-[1.05]">
            Appointment requests
          </h1>
          <div className="flex items-center gap-3">
            {lastUpdated && (
              <span className="text-[12px] text-[#1E1B18]/50">
                Updated {formatUpdated(lastUpdated)}
              </span>
            )}
            <button
              onClick={() => loadBookings()}
              disabled={refreshing}
              className="inline-flex items-center gap-2 border border-[#1E1B18]/20 hover:border-[#1E1B18]/50 disabled:opacity-50 text-[#1E1B18] px-4 py-2 rounded-full text-[12px] font-medium transition-colors"
            >
              <RefreshCw size={13} className={refreshing ? 'animate-spin' : ''} />
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-8">
          {['all', 'pending', 'confirmed', 'declined'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-full text-[12px] font-medium capitalize border transition-colors ${
                filter === f
                  ? 'bg-[#0F0E0C] text-[#F4EFE7] border-[#0F0E0C]'
                  : 'bg-transparent text-[#1E1B18]/70 border-[#1E1B18]/20 hover:border-[#1E1B18]/50'
              }`}
            >
              {f} {f !== 'all' && `(${bookings.filter(b => b.status === f).length})`}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-[14px] text-[#1E1B18]/60 py-16 text-center">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="text-[14px] text-[#1E1B18]/60 py-16 text-center border border-dashed border-[#1E1B18]/15 rounded-sm bg-[#FBF7F0]">
            No {filter === 'all' ? '' : filter} requests yet.
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((b) => (
              <div key={b.booking_id} className="bg-[#FBF7F0] border border-[#1E1B18]/8 rounded-sm p-6 flex flex-col md:flex-row md:items-center gap-5 md:gap-8">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="text-[15px] font-medium text-[#1E1B18] capitalize">
                      {b.service} · {b.duration} min
                    </div>
                    <span className={`text-[10px] tracking-[0.15em] font-medium px-2.5 py-0.5 rounded-full uppercase ${statusStyle[b.status] || statusStyle.pending}`}>
                      {b.status}
                    </span>
                  </div>
                  <div className="text-[13px] text-[#1E1B18]/70 flex flex-wrap items-center gap-x-5 gap-y-1">
                    <span className="flex items-center gap-1.5"><Calendar size={13} /> {b.date} at {b.time}</span>
                    <span>{b.name}</span>
                    <span className="flex items-center gap-1.5"><Mail size={12} /> {b.email}</span>
                    {b.phone && <span className="flex items-center gap-1.5"><Phone size={12} /> {b.phone}</span>}
                  </div>
                </div>

                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => updateStatus(b.booking_id, 'confirmed')}
                    disabled={b.status === 'confirmed'}
                    className="inline-flex items-center gap-1.5 bg-[#0F0E0C] hover:bg-[#2A2622] disabled:opacity-40 disabled:cursor-not-allowed text-[#F4EFE7] px-4 py-2 rounded-full text-[12px] font-medium transition-colors"
                  >
                    <Check size={13} /> Confirm
                  </button>
                  <button
                    onClick={() => updateStatus(b.booking_id, 'declined')}
                    disabled={b.status === 'declined'}
                    className="inline-flex items-center gap-1.5 border border-[#1E1B18]/20 hover:border-[#1E1B18]/50 disabled:opacity-40 disabled:cursor-not-allowed text-[#1E1B18] px-4 py-2 rounded-full text-[12px] font-medium transition-colors"
                  >
                    <X size={13} /> Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {checked && <AdminArticles />}
      {checked && <AdminGiftCards />}
      {checked && <AdminSearchSetup />}

      <Footer />
    </div>
  );
};

export default Admin;
